import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { auth } from 'services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { schoolsApi } from 'services/apiDigitalTicket/schools';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import ENVS from 'constants/enviroments';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as SchoolsActions from './actions';

// Types
import { SchoolsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof SchoolsActions.listSchoolsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando instituições' }),
    );
    const isCsvFull = (payload as { isCsvFull?: boolean })?.isCsvFull;
    delete (payload as { isCsvFull?: boolean })?.isCsvFull;
    const response = yield call(schoolsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SchoolsActions.listSchoolsSuccess({
          schoolsList: response.data,
          schoolsListFull: response.data,
          isCsvFull,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - listSchools - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.listSchoolsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof SchoolsActions.getSchoolRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(schoolsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(SchoolsActions.getSchoolSuccess({ schoolData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - getSchool - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.getSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof SchoolsActions.filterSchoolsRequest>): any {
  try {
    const response = yield call(schoolsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SchoolsActions.filterSchoolsSuccess({
          schoolsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      // yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - filterSchools - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.filterSchoolsFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* filterCourse({
  payload,
  meta,
}: ReturnType<typeof SchoolsActions.filterCoursesRequest>): any {
  try {
    const response = yield call(schoolsApi.getCourse, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SchoolsActions.filterCoursesSuccess({
          coursesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('course - filterCourses - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.filterCoursesFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<typeof SchoolsActions.createSchoolRequest>): any {
  try {
    const { body, redirectTo } = payload;
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando instituição' }),
    );
    const response = yield call(schoolsApi.create, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield call(() =>
        sendPasswordResetEmail(auth, response.data.email, {
          url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
        }),
      );

      yield put(
        SchoolsActions.createSchoolSuccess({ schoolData: response.data }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message:
            'Instituição cadastrada com sucesso. E-mail de definição de senha enviado.',
        }),
      );

      yield put(DialogsActions.setClose());

      if (redirectTo) yield put(push(redirectTo));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - createSchool - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.createSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof SchoolsActions.updateSchoolRequest>): any {
  const { body, redirectTo } = payload;
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando instituição' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(schoolsApi.update, {
      data: {
        ...(transportOperator.id && {
          transportOperatorId: transportOperator.id,
        }),
        ...body,
      },
    });

    if (response.status === 200) {
      yield put(
        SchoolsActions.updateSchoolSuccess({ schoolData: response.data }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Instituição editada com sucesso',
        }),
      );

      if (redirectTo) yield put(push(redirectTo));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - updateSchool - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.updateSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateReportLimit({
  payload,
}: ReturnType<typeof SchoolsActions.updateSchoolReportLimitRequest>): any {
  const { body, redirectTo } = payload;
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Salvando alterações' }));
    const response = yield call(schoolsApi.updateReportLimit, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield put(
        SchoolsActions.updateSchoolReportLimitSuccess({
          schoolData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Alterações salvas com sucesso',
        }),
      );

      if (redirectTo) yield put(push(redirectTo));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - updateSchoolReportLimit - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.updateSchoolReportLimitFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof SchoolsActions.deleteSchoolRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo instituição' }),
    );
    const response = yield call(schoolsApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(SchoolsActions.deleteSchoolSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Instituição excluída com sucesso',
        }),
      );
      yield put(SchoolsActions.listSchoolsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schools - deleteSchool - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.deleteSchoolFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listCourses({
  payload,
}: ReturnType<typeof SchoolsActions.listCoursesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando curso' }));
    const response = yield call(schoolsApi.getCourse, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        SchoolsActions.listCoursesSuccess({ coursesList: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('course - listCourses - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.listCoursesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createCourse({
  payload,
}: ReturnType<typeof SchoolsActions.createCourseRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Cadastrando curso' }));
    const response = yield call(schoolsApi.createCourse, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(SchoolsActions.createCourseSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Curso cadastrada com sucesso.',
        }),
      );
      yield put(SchoolsActions.listCoursesRequest({}));

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('course - createCourse - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.createCourseFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateCourse({
  payload,
}: ReturnType<typeof SchoolsActions.updateCourseRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando curso' }));
    const response = yield call(schoolsApi.updateCourse, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(SchoolsActions.updateCourseSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Curso editada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('course - updateCourse - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.updateCourseFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeCourse({
  payload,
}: ReturnType<typeof SchoolsActions.deleteCourseRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo curso' }));
    const response = yield call(schoolsApi.deleteCourse, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(SchoolsActions.deleteCourseSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Curso excluída com sucesso',
        }),
      );
      yield put(SchoolsActions.listSchoolsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('course - deleteCourse - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SchoolsActions.deleteCourseFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const schoolsSagas = [
  takeLatest(SchoolsTypes.LIST_SCHOOLS_REQUEST, list),
  takeLatest(SchoolsTypes.GET_SCHOOL_REQUEST, get),
  takeLatest(SchoolsTypes.FILTER_SCHOOLS_REQUEST, filter),
  takeLatest(SchoolsTypes.CREATE_SCHOOL_REQUEST, create),
  takeLatest(SchoolsTypes.UPDATE_SCHOOL_REQUEST, update),
  takeLatest(
    SchoolsTypes.UPDATE_SCHOOL_REPORT_LIMIT_REQUEST,
    updateReportLimit,
  ),
  takeLatest(SchoolsTypes.DELETE_SCHOOL_REQUEST, remove),
  takeLatest(SchoolsTypes.LIST_COURSES_REQUEST, listCourses),
  takeLatest(SchoolsTypes.CREATE_COURSE_REQUEST, createCourse),
  takeLatest(SchoolsTypes.UPDATE_COURSE_REQUEST, updateCourse),
  takeLatest(SchoolsTypes.DELETE_COURSE_REQUEST, removeCourse),
  takeLatest(SchoolsTypes.FILTER_COURSES_REQUEST, filterCourse),
];
